import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';

import {
  CreateDirectSaleRequest,
  DirectSaleAuthorizationOrCancelation,
  DirectSaleStatisticsRequest,
  UpdateDirectSaleRequest,
} from './DirectSale.types';
import {
  authorizeDirectSale,
  cancelDirectSale,
  createDirectSale,
  deleteDirectSale,
  directSaleApplyTotalDiscount,
  directSaleIncludeTag,
  getDirectSale,
  listDirectSale,
  saleStatistics,
  updateDirectSale,
} from './DirectSale.service';
import { PageRequest } from '@/core/request/PageRequest';
import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { useIntl } from 'react-intl';
import { notification } from 'antd';
import notificationError from '@/utils/functions/notificationError';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';

export function useDirectSales(params?: PageRequest) {
  const query = useInfiniteQuery(
    ['direct-sales', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listDirectSale(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useDirectSale(id?: number) {
  const query = useQuery(['direct-sale', id], () =>
    id ? getDirectSale(id) : undefined,
  );

  useServiceError(query.error);

  return query;
}

export function useDirectSaleDetail() {
  const query = useMutation(getDirectSale, {
    onError(err) {
      notificationError(err);
    },
  });

  useServiceError(query.error);

  return query;
}

export function useCreateDirectSale() {
  const intl = useIntl();
  const mutation = useMutation(
    (obj: CreateDirectSaleRequest) => createDirectSale(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'directSale.create.notification.success',
          }),
          description: intl.formatMessage({
            id: 'directSale.create.notification.successDescription',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useDeleteDirectSale() {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const mutation = useMutation((id: number) => deleteDirectSale(id), {
    onSuccess() {
      queryClient.refetchQueries(['direct-sales']);

      notification.success({
        message: intl.formatMessage({
          id: 'directSale.notifications.remove.success.title',
        }),
        description: intl.formatMessage({
          id: 'directSale.notifications.remove.success.description',
        }),
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateDirectSale() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: UpdateDirectSaleRequest) => updateDirectSale(obj),
    {
      onSuccess(_, res) {
        notification.success({
          message: intl.formatMessage({
            id: 'directSale.notifications.update.success.title',
          }),
          description: intl.formatMessage({
            id: 'directSale.notifications.update.success.description',
          }),
        });

        queryClient.invalidateQueries(['direct-sale', res.id]);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useAuthorizeDirectSale(saleId?: number) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const mutation = useMutation(
    (obj: DirectSaleAuthorizationOrCancelation) =>
      authorizeDirectSale(obj, saleId),
    {
      onSuccess(data) {
        queryClient.invalidateQueries(['direct-sale', data.id]);

        notification.success({
          message: intl.formatMessage({
            id: 'directSale.notifications.authorizeSale.success.title',
          }),
          description: intl.formatMessage({
            id: 'directSale.notifications.authorizeSale.success.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useCancelDirectSale(saleId?: number) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: DirectSaleAuthorizationOrCancelation) =>
      cancelDirectSale(obj, saleId),
    {
      onSuccess(data) {
        queryClient.invalidateQueries(['direct-sale', data.id]);

        notification.success({
          message: intl.formatMessage({
            id: 'directSale.notifications.cancelSale.success.title',
          }),
          description: intl.formatMessage({
            id: 'directSale.notifications.cancelSale.success.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useDirectSaleStatistics(params?: DirectSaleStatisticsRequest) {
  return useQuery(
    ['sales/saleStatistics', { ...params }],
    () => saleStatistics(params),
    {
      enabled:
        !!params?.companyIds?.length &&
        !!params?.endDate &&
        !!params?.startDate,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useDirectSaleIncludeTag() {
  const queryChanel = useQueryChanel();

  return useMutation(directSaleIncludeTag, {
    onSuccess(_, variables) {
      notification.success({
        message: `Etiqueta ${
          !!variables.tagId ? 'vinculada' : 'desvinculada'
        } da venda direta`,
      });

      queryChanel({
        invalidateQueries: [['direct-sale', variables.saleId]],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useDirectSaleApplyTotalDiscount() {
  const queryChanel = useQueryChanel();

  return useMutation(directSaleApplyTotalDiscount, {
    async onSuccess(_, variables) {
      notification.success({
        message: 'Sucesso',
        description: 'Descontos atualizados',
      });

      queryChanel({
        invalidateQueries: [['direct-sale', variables?.saleId]],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}
